import { Configurations } from '../AemConfigurationTypes';
import { DeliveryPromise } from '../DeliveryPromise';
import { FilterContentTypes, SearchResultsExecutionStatusEnums } from '../enums';
import { StringStringMap } from '../GeneralTypes';
import { ProductCategory } from '../product-detail';
import { ProductPrices, ProductPriceViewModel } from '../product-detail/ProductPriceTypes';
import { ProductFeaturesComparisonFeature } from '../services/productService';
import { UrlState } from '../UrlTypes';

import { FilterState, InStoreFilter } from './FilterTypes';
import { FilterWizardConfig } from './FilterWizardTypes';

export type ListerItem = Banner | ProductLister;

export type ProductListerRenderData = {
  configs: Configurations;
  store: {
    urlState: UrlState;
    lister?: ProductSearchObject;
    filters?: FilterState;
  };
};

export type ProductListerOptions = {
  filterWizard: FilterWizardConfig;
  partialSearchResults: PartialSearchResults;
  enableJsonLd: boolean;
  parentPage: {
    title: string;
    href: string;
  };
  quickLinks?: Array<ListerQuickLinksType>;
  seoTextIncludePath: string;
  contentBlocks?: Array<ListerContentBlockType>;
  title?: string;
  hideTitle?: boolean;
  seoTextLocation?: string;
};

export type ProductLister = {
  active?: boolean;
  uniqueId?: string;
  availability: number;
  brand: string;
  colourId: number;
  colours: Array<ProductColor>;
  currency: string;
  featureValueGenderId: number | null;
  genderCode: string;
  impression: ProductListerImpression;
  mainWebShopId: number;
  mainWebShopName: string;
  productCode: string;
  productId: number;
  productRating?: number | null;
  productReviews?: number | null;
  deliveryPromise?: DeliveryPromise;
  seoUrl: string;
  sku?: number;
  title: string;
  type: string;
  ccExcluded?: boolean;
  hdExcluded?: boolean;
  selectedSku?: string;
  defaultCategoryId?: number;
  category?: ProductCategory;
  sustainable?: boolean;
  ourPlanet?: boolean;
  isFallbackProduct?: boolean;
  alwaysVisible?: boolean;
  description?: string;
  features?: Array<ProductFeaturesComparisonFeature>;
  brandId?: number;
  isSponsored?: boolean;
  adId?: string;
};

export type ProductListerImpression = {
  brand: string;
  category: string;
  id: number;
  name: string;
  price: string;
  variant: string;
};

export type ProductColorSize = {
  sku: string;
  code: string;
  order: number;
  commercialSize?: string;
  commercialSizeDefault?: string;
  fullCommercialSize?: string;
  prices?: ProductPrices;
  productDiscountPercentage?: number;
  productDiscountAmount?: number;
  availability?: number;
  exactAvailability?: null | number;
  lastPieces?: boolean;
  storeSourceExcluded?: boolean;
  active?: boolean;
  historicAvailable?: boolean;
};

export type ProductColor = {
  alwaysVisible: boolean;
  availability: number;
  colourCode: string;
  colourId: number;
  description: string;
  extendedRange: boolean;
  hasDiscount: boolean;
  hasDifferentPrices: boolean | null;
  hoverImage?: string;
  image: string;
  allColourImages?: Array<string>;
  inWishList?: boolean;
  isCompareProduct?: boolean;
  markdown: boolean;
  swatch: string;
  price: {
    standardPrice?: number;
    sellPrice?: number;
    emPrice?: number;
    priceState?: number;
    productDiscountPercentage: number | null;
    productDiscountAmount: number | null;
  };
  priceOverlay?: {
    overlayCode: string;
    overlayThumb: string;
    idOverlayBackground?: number;
    overlayBackgroundCodeDescription?: string;
  };
  sizes: Array<ProductColorSize>;
  prices: {
    SELL: ProductPriceViewModel;
    STD?: ProductPriceViewModel;
    RRP?: ProductPriceViewModel;
    LOWEST?: ProductPriceViewModel;
  };
  webOnly?: boolean;
  newProduct?: boolean;
  promoDiscountPresent?: boolean;
};

export type ProductListerSwatch = {
  colourId: number;
  color: string;
  extendedRange: boolean;
  swatch: string;
  selected: boolean;
  hasDiscount: boolean;
  hasPermanentDiscount: boolean;
  outOfStock?: boolean;
};

export interface FilterValue {
  name: string;
  filterKey: string;
  itemCount?: number;
  active: boolean;
}

export interface Filter {
  name: string;
  type: string;
  filterOn: string;
  values: Array<FilterValue>;
  filterContentType: FilterContentTypes | null;
  rangeMin?: number;
  rangeMax?: number;
  boundaryMin?: number;
  boundaryMax?: number;
  itemsToShow?: number;
  visibleSearchBox?: string;
  displayHint?: Array<string>;
  disabled?: boolean;
  isActive?: boolean;
}

export interface BannerImage {
  name: string;
  image: string;
  url: string;
  alt: string;
}

export interface Banner {
  listerPosition: number;
  firstPageOnly?: number;
  images: Array<BannerImage>;
  isSponsored?: boolean;
  adId?: string;
}

export type ListerQuickLinksType = {
  linkName: string;
  linkPath: string;
  queryStringParams: string;
  target: string;
};

export type ListerContentBlockType = {
  linkPath: string;
  visibleInRow: number;
  visibleOnPage?: number;
  content?: string;
};

export type FredhopperGlossaryItem = {
  title: string;
  description: string;
};

export type FredhopperGlossaryItemObject = FredhopperGlossaryItem & {
  triggers: Array<string>;
};

export type PartialSearchResultsHeader = {
  title: string;
  description: string;
};

export type PartialSearchResults = {
  alternativeBrand: PartialSearchResultsHeader;
  changedMisspelling: PartialSearchResultsHeader;
  maybeLike: PartialSearchResultsHeader;
  maybeMeant: PartialSearchResultsHeader;
};

export type SearchPass = {
  name: string;
  itemCount: number;
};

export type ProductSearchObject = {
  banners?: Array<Banner>;
  filters?: Array<Filter>;
  itemCount?: number;
  items?: Array<Banner | ProductLister>;
  redirect?: boolean;
  title?: string;
  hideTitle?: boolean;
  quickLinks?: Array<ListerQuickLinksType>;
  contentBlocks?: Array<ListerContentBlockType>;
  seoTextInclude?: string;
  seoTextLocation?: string;
  redirectUrl?: string | null;
  segment?: string;
  totalPages?: number;
  isLoading?: boolean;
  fredhopperGlossary?: Array<FredhopperGlossaryItemObject>;
  filterWizard?: FilterWizardConfig;
  inStoreFilter?: InStoreFilter;
  executionStatus?: SearchResultsExecutionStatusEnums;
  alternativeQueryResults?: Array<AlternativeQueryResults>;
  productGroupUniqueIds?: StringStringMap;
  partialSearchResults?: PartialSearchResults;
  personalized?: boolean;
  traits?: Array<string>;
  isCSR?: boolean;
  isLoaded?: boolean;
  enableJsonLd?: boolean;
  parentPage?: {
    href: string;
    title: string;
  };
  searchPasses?: Array<SearchPass>;
};

export type ProductSwatchType = {
  hasDiscount: boolean;
  hasPermanentDiscount?: boolean;
  markdown?: boolean;
};

export type AlternativeQueryResults = {
  alternativeQuery: string;
  items?: Array<ProductLister>;
};

export function isBanner(item: Banner | ProductLister): item is Banner {
  return (item as Banner).listerPosition !== undefined;
}

export function isProduct(item: Banner | ProductLister): item is ProductLister {
  return (item as ProductLister).productId !== undefined;
}
